import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { ArrowLeft, Mail, Calendar, User, Building, FlaskConical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInquiries = ({ onBack }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact/inquiries`);
      setInquiries(response.data.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      ));
      setError(null);
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      setError('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading inquiries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-red-600 text-center">{error}</p>
            <Button onClick={fetchInquiries} className="mt-4 w-full">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={onBack}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Website
          </Button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Contact Inquiries</h1>
          <p className="text-gray-600">Total inquiries: {inquiries.length}</p>
        </div>

        {/* Inquiries List */}
        {inquiries.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-gray-500">
              No inquiries yet
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => (
              <Card key={inquiry.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <User className="w-5 h-5 text-green-600" />
                        {inquiry.name}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${inquiry.email}`} className="hover:text-green-600">
                          {inquiry.email}
                        </a>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(inquiry.timestamp)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {inquiry.company && (
                      <div className="flex items-start gap-2">
                        <Building className="w-4 h-4 text-gray-400 mt-1" />
                        <div>
                          <div className="text-sm font-semibold text-gray-700">Company</div>
                          <div className="text-sm text-gray-600">{inquiry.company}</div>
                        </div>
                      </div>
                    )}
                    
                    {inquiry.sampleId && (
                      <div className="flex items-start gap-2">
                        <FlaskConical className="w-4 h-4 text-gray-400 mt-1" />
                        <div>
                          <div className="text-sm font-semibold text-gray-700">Sample ID</div>
                          <div className="text-sm text-gray-600">{inquiry.sampleId}</div>
                        </div>
                      </div>
                    )}
                    
                    {inquiry.material && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700">Material</div>
                        <div className="text-sm text-gray-600">{inquiry.material}</div>
                      </div>
                    )}
                    
                    {inquiry.temperature && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700">Temperature</div>
                        <div className="text-sm text-gray-600">{inquiry.temperature}</div>
                      </div>
                    )}
                    
                    {inquiry.duration && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700">Duration</div>
                        <div className="text-sm text-gray-600">{inquiry.duration}</div>
                      </div>
                    )}
                  </div>
                  
                  {inquiry.message && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Message</div>
                      <div className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                        {inquiry.message}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiries;
